import { createContext, useContext, useEffect, useState, type ReactNode } from "react";
import { Toaster } from "sonner";

export type Theme = "light" | "dark" | "system";
const ThemeContext = createContext<{ theme: Theme; setTheme: (value: Theme) => void }>({ theme: "system", setTheme: () => {} });

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(() => {
    try { const saved = localStorage.getItem("pingys-theme"); return saved === "light" || saved === "dark" ? saved : "system"; }
    catch { return "system"; }
  });
  const [dark, setDark] = useState(document.documentElement.dataset.theme === "dark");
  useEffect(() => {
    const media = window.matchMedia("(prefers-color-scheme: dark)");
    const apply = () => {
      const resolved = theme === "system" ? media.matches : theme === "dark";
      document.documentElement.dataset.theme = resolved ? "dark" : "light";
      setDark(resolved);
    };
    apply();
    try { localStorage.setItem("pingys-theme", theme); } catch { /* Preference still works for this session. */ }
    media.addEventListener("change", apply);
    return () => media.removeEventListener("change", apply);
  }, [theme]);
  return <ThemeContext.Provider value={{ theme, setTheme }}>{children}<Toaster theme={dark ? "dark" : "light"} richColors position="top-right" /></ThemeContext.Provider>;
}

export const useTheme = () => useContext(ThemeContext);
